import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, IconButton, Link, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit, OpenInNew } from '@mui/icons-material';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { toastService } from '@services/toast.service.ts';
import { pmSourcesService, Source, SourceInput } from '@services/pm-sources.service.ts';

const EMPTY_FORM: SourceInput = { slug: '', title: '', authors: '', publicationInfo: '', url: '' };

// Мірорить SourcesService.buildSlug (api/src/modules/sources/sources.service.ts) - лише для живої
// підказки в полі; остаточну унікальність (суфікс a/b/c при колізії) гарантує бекенд.
const suggestSlug = (authors?: string | null, publicationInfo?: string | null): string => {
  const firstWord = (authors ?? '').trim().split(/[\s,]+/)[0] || '';
  const surname = firstWord.replace(/[^a-zA-Zа-яА-ЯіІїЇєЄ]/g, '').toLowerCase() || '';
  const yearMatch = (publicationInfo ?? '').match(/\b(19|20)\d{2}\b/);
  return `${surname}${yearMatch ? yearMatch[0] : ''}`;
};

const PmSourcesPageContent: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [sources, setSources] = useState<Source[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<SourceInput>(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Source | null>(null);

  const fetchSources = useCallback(async () => {
    setLoading(true);
    try {
      const result = await pmSourcesService.getSources({ page, limit, search: searchQuery || undefined });
      setSources(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити каталог джерел.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery]);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const openCreateDialog = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSlugTouched(false);
    setDialogOpen(true);
  };

  const openEditDialog = (source: Source) => {
    setEditingId(source.id);
    setForm({ slug: source.slug, title: source.title, authors: source.authors ?? '', publicationInfo: source.publicationInfo ?? '', url: source.url });
    setSlugTouched(true); // вже наявний slug - не перезаписуємо підказкою при редагуванні полів
    setDialogOpen(true);
  };

  // Поки PM не торкнувся поля slug вручну - система сама пропонує його з автора/публікації.
  const updateFormField = (patch: Partial<SourceInput>) => {
    const next = { ...form, ...patch };
    if (!slugTouched) {
      next.slug = suggestSlug(next.authors, next.publicationInfo);
    }
    setForm(next);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.url.trim()) {
      toastService.error('Назва і посилання — обов\'язкові.');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await pmSourcesService.updateSource(editingId, form);
        toastService.success('Джерело оновлено.');
      } else {
        await pmSourcesService.createSource(form);
        toastService.success('Джерело додано.');
      }
      setDialogOpen(false);
      await fetchSources();
    } catch {
      toastService.error('Не вдалося зберегти джерело.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await pmSourcesService.deleteSource(deleteTarget.id);
      toastService.success('Джерело видалено.');
      setDeleteTarget(null);
      await fetchSources();
    } catch {
      toastService.error('Не вдалося видалити джерело.');
    }
  };

  return (
    <Box>
      <PageCard>
        <PageToolbar
          storageKey="filters:pm-sources"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за назвою або авторами',
              value: searchQuery,
              onChange: (v) => { setSearchQuery(v); setPage(1); },
              flexGrow: 1,
            },
          ]}
          onReset={() => { setSearchQuery(''); setPage(1); }}
          extraActions={
            <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
              Додати джерело
            </Button>
          }
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : sources.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>Джерел не знайдено.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Slug</TableCell>
                  <TableCell>Назва</TableCell>
                  <TableCell>Автори</TableCell>
                  <TableCell>Публікація</TableCell>
                  <TableCell>Посилання</TableCell>
                  <TableCell align="right">Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sources.map((source) => (
                  <TableRow key={source.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{source.slug}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>{source.title}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{source.authors || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{source.publicationInfo || '—'}</TableCell>
                    <TableCell>
                      <Link href={source.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        Відкрити <OpenInNew sx={{ fontSize: 14 }} />
                      </Link>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Редагувати">
                        <IconButton size="small" onClick={() => openEditDialog(source)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Видалити">
                        <IconButton size="small" onClick={() => setDeleteTarget(source)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:pm-sources"
        />
      </PageCard>

      <BaseDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editingId ? 'Редагувати джерело' : 'Додати джерело'}
        maxWidth="sm"
        actions={<Button variant="contained" onClick={handleSave} disabled={saving}>Зберегти</Button>}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
          <TextField
            label="Назва"
            fullWidth
            required
            value={form.title}
            onChange={(e) => updateFormField({ title: e.target.value })}
          />
          <TextField
            label="Автори"
            fullWidth
            value={form.authors ?? ''}
            onChange={(e) => updateFormField({ authors: e.target.value })}
          />
          <TextField
            label="Публікація (журнал, рік, сторінки)"
            fullWidth
            value={form.publicationInfo ?? ''}
            onChange={(e) => updateFormField({ publicationInfo: e.target.value })}
          />
          <TextField
            label="Slug (код джерела)"
            helperText="Система пропонує сама з автора+року - можна скоригувати вручну."
            fullWidth
            value={form.slug ?? ''}
            onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value }); }}
          />
          <TextField
            label="Посилання (URL)"
            fullWidth
            required
            value={form.url}
            onChange={(e) => updateFormField({ url: e.target.value })}
          />
        </Box>
      </BaseDialog>

      <ConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Видалити джерело?"
        message={deleteTarget ? `«${deleteTarget.title}» буде видалено з каталогу.` : ''}
        confirmText="Видалити"
      />
    </Box>
  );
};

export const PmSourcesPage: React.FC = () => (
  <DashboardLayout>
    <PmSourcesPageContent />
  </DashboardLayout>
);
